import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useTheme } from '../../../contexts/ThemeContext';

const TestimonialsSlider = () => {
  const { isDarkMode } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: "Ernest Abongho Tunguleh",
      role: "Local Guide · 51 reviews",
      location: "Coimbatore",
      avatar: "https://ui-avatars.com/api/?name=Ernest+Abongho&background=1e3a8a&color=fff&size=128",
      rating: 5,
      content: "I tried it out of curiosity being new in Coimbatore, but I came out being an ambassador for them. Customer interest is their priority.",
      serviceUsed: "Laundry Service"
    },
    {
      id: 2,
      name: "Santhosh Kumar",
      role: "Customer",
      location: "Coimbatore",
      avatar: "https://ui-avatars.com/api/?name=Santhosh+Kumar&background=1e3a8a&color=fff&size=128",
      rating: 5,
      content: "Quick and reliable service. Express delivery was on time and clothes were fresh and well packed. Thank you Maayo Fabric Care!",
      serviceUsed: "Express Delivery"
    },
    {
      id: 3,
      name: "K Srinivasan",
      role: "Local Guide · 18 reviews",
      location: "Coimbatore",
      avatar: "https://ui-avatars.com/api/?name=K+Srinivasan&background=1e3a8a&color=fff&size=128",
      rating: 5,
      content: "Very good experience. Curtains came back neatly dry cleaned and ironed with perfect pleats. Highly recommended. Prompt service and awesome work, particularly the owner (lady) who is very polite. Delivery person (Sekar) does a wonderful job. I have recommended them to my friends circle and society.",
      serviceUsed: "Curtain Dry Cleaning"
    },
    {
      id: 4,
      name: "Srinivasan Jini",
      role: "Customer",
      location: "Coimbatore",
      avatar: "https://ui-avatars.com/api/?name=Srinivasan+Jini&background=1e3a8a&color=fff&size=128",
      rating: 5,
      content: "Maayo Fabric Care provides excellent laundry and dry cleaning service. The quality of cleaning is very good and clothes are handled with care. They are also offering special discounts for students, which is really helpful. Highly recommended for anyone looking for reliable and professional fabric care service!",
      serviceUsed: "Laundry & Dry Cleaning"
    },
    {
      id: 5,
      name: "Sekar Thambi",
      role: "Customer",
      location: "Coimbatore",
      avatar: "https://ui-avatars.com/api/?name=Sekar+Thambi&background=1e3a8a&color=fff&size=128",
      rating: 5,
      content: "Professional service with a personal touch. Maayo Laundry understands the importance of hygiene and cleanliness, especially for medical professionals. Their sanitization process gives me complete confidence.",
      serviceUsed: "Professional Laundry"
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials?.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials?.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000); // Resume auto-play after 10 seconds
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? testimonials?.length - 1 : currentIndex - 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === testimonials?.length - 1 ? 0 : currentIndex + 1);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < rating ? "text-amber-400 fill-current" : isDarkMode ? "text-slate-600" : "text-gray-300"}
      />
    ));
  };

  return (
    <section className={`py-20 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
        : 'bg-gradient-to-br from-primary/5 via-background to-accent/5'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            isDarkMode ? 'text-white' : 'text-foreground'
          }`}>
            What Our Customers Say
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDarkMode ? 'text-slate-300' : 'text-muted-foreground'
          }`}>
            Real experiences from real customers across Coimbatore
          </p>
        </motion.div>

        {/* Testimonials Slider */}
        <div className="relative max-w-4xl mx-auto">
          {/* Main Testimonial Card */}
          <div className="relative min-h-[500px] sm:min-h-[480px] overflow-visible">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="absolute inset-0"
              >
                <div className={`rounded-2xl p-8 sm:p-12 shadow-elevation-2 border h-full flex flex-col ${
                  isDarkMode 
                    ? 'bg-slate-800/50 border-slate-700/50' 
                    : 'bg-card border-border'
                }`}>
                  
                  {/* Top Section (quote + text) */}
                  <div className="flex-1">
                    {/* Quote Icon */}
                    <div className="mb-6">
                      <Icon name="Quote" size={40} className={isDarkMode ? "text-white/30" : "text-primary/20"} />
                    </div>

                    {/* Testimonial Content */}
                    <p className={`text-lg sm:text-xl leading-relaxed mb-8 ${
                      isDarkMode ? 'text-white' : 'text-foreground'
                    }`}>
                      "{testimonials?.[currentIndex]?.content}"
                    </p>

                    {/* Rating + Service */}
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="flex space-x-1">
                        {renderStars(testimonials?.[currentIndex]?.rating)}
                      </div>
                      <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                        isDarkMode 
                          ? 'bg-blue-600/20 text-blue-400' 
                          : 'bg-primary/10 text-primary'
                      }`}>
                        <Icon name="Package" size={14} />
                        <span>{testimonials?.[currentIndex]?.serviceUsed}</span>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Section (user info) */}
                  <div className="flex items-center space-x-4 mt-6">
                    <div className="w-14 h-14 rounded-full overflow-hidden shadow-elevation-1">
                      <Image
                        src={testimonials?.[currentIndex]?.avatar}
                        alt={testimonials?.[currentIndex]?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h4 className={`text-lg font-semibold ${
                        isDarkMode ? 'text-white' : 'text-foreground'
                      }`}>
                        {testimonials?.[currentIndex]?.name}
                      </h4>
                      <p className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-muted-foreground'
                      }`}>
                        {testimonials?.[currentIndex]?.role}
                      </p>
                      <p className={`text-xs flex items-center space-x-1 ${
                        isDarkMode ? 'text-slate-400' : 'text-muted-foreground'
                      }`}>
                        <Icon name="MapPin" size={12} />
                        <span>{testimonials?.[currentIndex]?.location}</span>
                      </p>
                    </div>
                  </div>

                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          {/* Navigation Controls */}
          <div className="flex items-center justify-center mt-8 space-x-4">
            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className={`w-12 h-12 border rounded-full flex items-center justify-center transition-colors duration-300 shadow-elevation-1 ${
                isDarkMode 
                  ? 'bg-slate-700/50 border-slate-600/50 hover:bg-slate-600/50' 
                  : 'bg-card border-border hover:bg-muted'
              }`}
              aria-label="Previous testimonial"
            >
              <Icon name="ChevronLeft" size={20} className={isDarkMode ? "text-slate-300" : "text-foreground"} />
            </button>

            {/* Dots Indicator */}
            <div className="flex space-x-2">
              {testimonials?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? (isDarkMode ? 'bg-blue-500 w-8' : 'bg-primary w-8')
                      : (isDarkMode ? 'bg-slate-600 hover:bg-slate-500' : 'bg-border hover:bg-muted-foreground')
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={goToNext}
              className={`w-12 h-12 border rounded-full flex items-center justify-center transition-colors duration-300 shadow-elevation-1 ${
                isDarkMode 
                  ? 'bg-slate-700/50 border-slate-600/50 hover:bg-slate-600/50' 
                  : 'bg-card border-border hover:bg-muted'
              }`}
              aria-label="Next testimonial"
            >
              <Icon name="ChevronRight" size={20} className={isDarkMode ? "text-slate-300" : "text-foreground"} />
            </button>
          </div>

          {/* Auto-play Indicator */}
          <div className="flex items-center justify-center mt-4">
            <div className={`flex items-center space-x-2 text-xs ${
              isDarkMode ? 'text-slate-400' : 'text-muted-foreground'
            }`}>
              <Icon name={isAutoPlaying ? "Play" : "Pause"} size={12} />
              <span>{isAutoPlaying ? "Auto-playing" : "Paused"}</span>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16"
        >
          <p className={`mb-6 ${
            isDarkMode ? 'text-slate-300' : 'text-muted-foreground'
          }`}>
            Join thousands of satisfied customers
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.href = '/contact-us'}
            className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors duration-300 shadow-elevation-2"
          >
            Start Your Experience
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSlider;